import { type ModeEnvType } from 'types/enums'

// Type for matches from MetaFunction
type MetaMatches = Array<{
  id: string
  data?: unknown
}>

// Helper to extract ENV from root loader data via matches
function getENVFromMatches(matches?: MetaMatches):
  | {
      MODE: ModeEnvType
      ALLOW_INDEXING: string | undefined
    }
  | undefined {
  if (!matches) return undefined

  // Find the root match (it's usually the first one or has id 'root')
  const rootMatch = matches.find((match) => match.id === 'root') || matches[0]
  if (!rootMatch?.data) return undefined

  // Type assertion to access ENV from root loader data
  const rootData = rootMatch.data as {
    ENV?: { MODE: ModeEnvType; ALLOW_INDEXING?: string }
  }
  const env = rootData.ENV
  if (!env) return undefined

  return {
    MODE: env.MODE,
    ALLOW_INDEXING: env.ALLOW_INDEXING,
  }
}

export function getMetaTags({
  matches,
  ENV,
  title,
  description,
  url,
  image,
  index = true,
}: {
  matches?: MetaMatches
  ENV?: {
    MODE: ModeEnvType
    ALLOW_INDEXING: string | undefined
  }
  title?: string
  description?: string
  url?: string
  image?: string
  index?: boolean
}) {
  const env = ENV || getENVFromMatches(matches)
  const isProduction = env?.MODE === 'production'

  const defaultOgImage = isProduction
    ? 'https://gamaoptima.com/img/og.jpg'
    : 'https://gama-optima-staging.fly.dev/img/og.jpg'

  const metaTags: Array<Record<string, unknown>> = [
    { title: title || 'Gama Óptima | Coches de Ocasión Premium en Barcelona' },
    {
      name: 'description',
      content:
        description ||
        'Concesionario de coches de ocasión premium en Barcelona. BMW, Mercedes, Audi, Porsche, Mini, Lexus, Volvo y más marcas. Calidad, garantía y trato personalizado en Barcelona.',
    },
    {
      name: 'keywords',
      content:
        'coches de ocasión premium, coches segunda mano barcelona, concesionario coches barcelona, bmw ocasión barcelona, mercedes ocasión barcelona, audi ocasión barcelona, porsche ocasión barcelona, mini segunda mano, lexus ocasión, volvo ocasión, volkswagen ocasión, coches premium barcelona, concesionario tres torres',
    },
    { name: 'author', content: 'Gama Óptima' },
    { 'http-equiv': 'Content-Language', content: 'es' },
    {
      property: 'og:title',
      content: title || 'Gama Óptima | Coches de Ocasión Premium en Barcelona',
    },
    {
      property: 'og:description',
      content:
        description ||
        'Concesionario premium de coches de ocasión en Barcelona. BMW, Audi, Mercedes, Porsche y más marcas con garantía.',
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: url ?? 'https://gamaoptima.com' },
    { property: 'og:site_name', content: 'Gama Óptima' },
    {
      property: 'og:image',
      content: image || defaultOgImage,
    },
    { name: 'twitter:card', content: 'summary_large_image' },
    {
      name: 'twitter:title',
      content: title || 'Gama Óptima | Coches de Ocasión Premium en Barcelona',
    },
    {
      name: 'twitter:description',
      content:
        description ||
        'Compra coches premium de ocasión en Barcelona. BMW, Mercedes, Audi, Porsche y más marcas.',
    },
    {
      name: 'twitter:image',
      content: image || defaultOgImage,
    },
  ]

  const allowIndexing = index && env?.ALLOW_INDEXING !== 'false'
  // Solo permitir indexación en producción
  if (isProduction && allowIndexing) {
    metaTags.push({ name: 'robots', content: 'index, follow' })
    if (url) {
      metaTags.push({ tagName: 'link', rel: 'canonical', href: url })
    }
  } else {
    metaTags.push({ name: 'robots', content: 'noindex, nofollow' })
  }

  return metaTags
}

export function getCarSlugMetaTags({
  car,
  domainUrl,
  matches,
  titlePrefix,
  index = true,
}: {
  car: {
    brand: {
      name: string
    }
    model: string
    description: string
    year: number
    matriculation?: number
    mileage: number
    slug: string
    images: {
      id: string
    }[]
    index?: boolean
  }
  domainUrl: string | undefined
  matches?: MetaMatches
  titlePrefix?: string
  index?: boolean
}) {
  if (!car) {
    return [
      { title: 'Coche no encontrado | Gama Óptima' },
      { name: 'robots', content: 'noindex, nofollow' },
    ]
  }

  let title = `${car.brand.name} ${car.model} ${car.description} (${car.matriculation ? new Date(car.matriculation).getFullYear() : car.year} - ${car.mileage.toLocaleString('es-ES')} km) | Gama Óptima`
  if (titlePrefix) {
    title = `${titlePrefix} ${title}`
  }

  const parsedDomainUrl = (domainUrl ?? 'https://gamaoptima.com').replace(
    /\/$/,
    '',
  )

  const url = `${parsedDomainUrl}/coche/${car.slug}`
  const image = car.images?.[0]?.id
    ? `${parsedDomainUrl}/resources/images/${car.images[0].id}/carImage.jpg`
    : undefined

  const description = `${car.brand.name} ${car.model} de ocasión, revisado y reacondicionado al detalle. Ahorra hasta un 35% y cómpralo con garantía en Gama Óptima Barcelona.`

  return getMetaTags({ matches, title, description, url, image, index })
}
